/* tslint:disable:object-literal-sort-keys no-console */
import {
  breadcrumbsIntegration,
  BrowserClient,
  captureException,
  captureMessage,
  dedupeIntegration,
  defaultStackParser,
  Event,
  getCurrentScope,
  globalHandlersIntegration,
  linkedErrorsIntegration,
  makeFetchTransport,
  setTags
} from '@sentry/browser';
import CONFIG from './config';
import { isExternalSdk } from './environment-utils';
// import { isFeatureEnable } from './features-enabling';
import { getCurrentMraidInfos } from './mraid-infos';
import { DebugLog } from './debug-log';

let shouldSendDatas = true;
let tags: any;

function getMajorVersion(sdkVersion: string = ''): string {
  const sdkVersionDetail = sdkVersion.split('.');
  return (sdkVersionDetail && sdkVersionDetail[0]) || 'unknown';
}

function isFormatError(url: string | undefined): boolean {
  if (!url) {
    return false;
  }
  const formatsUriContains = [
    'ogyfmts.com',
    'litecdn.com',
    'presage.io',
    'ogury.io',
    'mraid-wrapper'
  ];
  for (let i = 0; i < formatsUriContains.length; i++) {
    if (url.indexOf(formatsUriContains[i]) !== -1) {
      return true;
    }
  }
  return false;
}

function getSentryIgnoreUrls() {
  return [/confiant-qa\.com/];
}

function getSentryIgnoreErrors() {
  return [
    /The video player does not support autoplay/,
    /Video failed to start playing/,
    /No prepared event timeout for video/,
    /Problem displaying a media file/,
    /\[object Event\]/,
    /Java exception was raised during method invocation/,
    /Java object is gone/,
    /NPObject/,
    /Unexpected number/,
    /Unexpected identifier/,
    /mraid is not defined/,
    /Invalid or unexpected token/,
    /mraidbridge is not defined/,
    /Cannot read property 'contentDocument' of undefined/,
    /webviewDidAppear is not defined/,
    /webviewDidClose is not defined/,
    /window.onReceive is not a function/,
    /HTML5 Video player error/,
    /Could not find a media file supported by the video player/,
    /prefetch video error/,
    /MRAID container error/,
    /onCustomEvent is not defined/,
    /ResizeObserver loop/
  ];
}

function getSentryUrl(formatName: FormatName) {
  if (formatName === 'Video' && CONFIG.SENTRY_URL_VIDEO_FORMATS) {
    return CONFIG.SENTRY_URL_VIDEO_FORMATS;
  }

  return CONFIG.SENTRY_URL_UNIVERSAL_FORMATS;
}

function getSampleRate(formatName: string) {
  return formatName === 'Video' || formatName === 'Display redirect'
    ? 0.05
    : 0.2;
}

export function initSentry(format: FormatName = 'unknown', context: AdContext) {
  // We exclude api keys to adress an issue with a publisher which inject custom js in the webview
  // we disable sentry on this publisher and on 12345 SSP to prevent many errors sended in sentry
  if (
    [
      '273976',
      '274316',
      '274389',
      '274390',
      '274391',
      '274392',
      '274393',
      '274394',
      '274395',
      '274493',
      '274494',
      '274888',
      '274889',
      '275752',
      '275753',
      '275864',
      '275915',
      '275929',
      '275930',
      '275955',
      '275956',
      '275957',
      '275958',
      '275970',
      '275971',
      '275972',
      '275973',
      '276000'
    ].indexOf(context.apiKey) > -1 ||
    ['12345'].indexOf(context._appExchange) > -1
  ) {
    return;
  }

  if (!CONFIG.SENTRY_URL_UNIVERSAL_FORMATS || !CONFIG.COMMIT_HASH) {
    DebugLog.log(
      'SENTRY_URL_UNIVERSAL_FORMATS and/or COMMIT_HASH env variables not found. Sentry monitoring not initialized.'
    );
    return;
  }

  tags = {
    ...context,
    format,
    sdkMajorVersion: getMajorVersion(context.sdkVersion),
    _delivery: isExternalSdk() ? 'external' : 'internal'
  };

  // Don't send AAID to Sentry
  delete tags.userId;
  // Don't send the instance token to Sentry
  delete tags.userInstanceToken;
  // Don't send gdpr information
  delete tags.gdpr;
  delete tags.tcfv2;

  const sentryURL = getSentryUrl(format);

  const sentryOptions = {
    dsn: sentryURL,
    release: CONFIG.COMMIT_HASH,
    sampleRate: getSampleRate(format),
    ignoreErrors: getSentryIgnoreErrors(),
    denyUrls: getSentryIgnoreUrls(),
    environment: CONFIG.ENV,
    integrations: [
      breadcrumbsIntegration(),
      globalHandlersIntegration(),
      linkedErrorsIntegration(),
      dedupeIntegration()
    ],
    transport: makeFetchTransport,
    stackParser: defaultStackParser
  };

  const bindClient = (event: Event) => {
    if (
      !isFormatError(event?.request?.url) &&
      getCurrentScope()
        .getClient()
        ?.getDsn() !== sentryScriptsClient.getDsn()
    ) {
      getCurrentScope().setClient(sentryScriptsClient);
    }
  };

  const client = new BrowserClient({
    ...sentryOptions,
    beforeSend: event => {
      if (!shouldSendDatas) {
        return null;
      }
      const currentMraidInfos: MraidInfos = getCurrentMraidInfos();
      event.tags = {
        ...event.tags,
        ...getMraidTagsInfos(currentMraidInfos)
      };
      bindClient(event);
      return event;
    }
  });

  const sentryScriptsClient = new BrowserClient({
    ...sentryOptions,
    dsn: CONFIG.SENTRY_URL_CLIENT_SCRIPTS
  });

  try {
    client.init();
  } catch (e) {
    DebugLog.log('Sentry init failed', { error: e });
  }
  setTags(tags);
}

export function stopSentry() {
  shouldSendDatas = false;
}

export function logError(error: string | Error | ErrorEvent, extra?: object) {
  DebugLog.log('Error', { ...extra, error });
  captureException(error, { tags: { ...extra } });
}

export function logMessage(
  message: string,
  extra?: object,
  level:
    | 'debug'
    | 'info'
    | 'error'
    | 'fatal'
    | 'warning'
    | 'log'
    | undefined = 'error'
) {
  DebugLog.log(message, { level, ...extra });
  if (window.isOgyPreview) {
    return;
  }
  captureMessage(message, { level, tags: { ...extra } });
}

function getMraidTagsInfos(mraidInfos: MraidInfos) {
  return {
    mraidState: mraidInfos.state || 'unknown',
    mraidIsViewable:
      typeof mraidInfos.isViewable !== 'undefined'
        ? mraidInfos.isViewable
        : 'unknown'
  };
}

export const __TESTS__ = {
  getMraidTagsInfos,
  isFormatError,
  getMajorVersion
};
